import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FiMail, FiPhone, FiMapPin, FiInstagram, FiVolume2, FiVolumeX, FiClock } from 'react-icons/fi';
import { FaWhatsapp, FaTiktok } from 'react-icons/fa';
import './Contact.css';

const Contact: React.FC = () => {
  const { t, i18n } = useTranslation();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);
  const [isMuted, setIsMuted] = useState(true);

  // Dil koduna göre video dosyası
  const videoSrc = (() => {
    const lang = i18n.language;
    const langMap: { [key: string]: string } = {
      tr: 'tr',
      en: 'en',
      de: 'de',
      ru: 'ru'
    };
    const langCode = langMap[lang] || 'tr';
    return `/contact-${langCode}1.mp4`;
  })();

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  // Dil değiştiğinde video yeniden yükle ve oynat
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.load();
      video.play().catch(() => {});
    }
  }, [videoSrc]);

  useEffect(() => {
    const video = videoRef.current;
    const section = sectionRef.current;
    
    if (!video || !section) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.3 }
    );

    observer.observe(section);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <section 
      ref={sectionRef} 
      className="contact section" 
      id="contact"
      style={{ backgroundImage: 'url(/iletisim.jpeg)' }}
    >
      <div className="contact-overlay"></div>
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="section-title">{t('contact.title')}</h2>
          <p className="section-subtitle">{t('contact.subtitle')}</p>
        </motion.div>

        <motion.div 
          className="contact-wrapper contact-with-video"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          {/* Sol taraf - Video */}
          <div className="contact-video-left">
            <div className="contact-video-item">
              <video
                ref={videoRef}
                src={videoSrc}
                muted
                loop
                playsInline
                preload="none"
              />
              <button className="video-mute-btn" onClick={toggleMute} aria-label={isMuted ? 'Unmute video' : 'Mute video'}>
                {isMuted ? <FiVolumeX /> : <FiVolume2 />}
              </button>
            </div>
          </div>

          {/* Sağ taraf - İletişim Bilgileri */}
          <div className="contact-info-card">
            <h3>{t('contact.info')}</h3>
            
            <div className="info-item">
              <div className="info-icon">
                <FiMapPin />
              </div>
              <div className="info-content">
                <strong>{t('contact.address')}</strong>
                <p>İlkbahar Mah. Sinpaş Altınoran Sitesi Kule 3 No: 196, Çankaya/Ankara</p>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">
                <FiPhone />
              </div>
              <div className="info-content">
                <strong>{t('contact.phone')}</strong>
                <a href="tel:+905337757181">+90 (533) 775 71 81</a>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">
                <FiClock />
              </div>
              <div className="info-content">
                <strong>{t('contact.workingHours')}</strong>
                <p>10:00 - 18:00</p>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">
                <FiMail />
              </div>
              <div className="info-content">
                <strong>{t('contact.email')}</strong>
                <a href="mailto:info@ustaconsulting.com">info@ustaconsulting.com</a>
              </div>
            </div>

            <a 
              href="https://wa.me/905337757181" 
              target="_blank" 
              rel="noopener noreferrer"
              className="whatsapp-btn"
            >
              <FaWhatsapp />
              {t('contact.whatsappBtn')}
            </a>

            <div className="social-links">
              <h4>{t('contact.followUs')}</h4>
              <div className="social-icons">
                <a href="https://www.instagram.com/ustaconsulting" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                  <FiInstagram />
                </a>
                <a href="https://www.tiktok.com/@ustaconsulting" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                  <FaTiktok />
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
